import { Box, Button, ButtonBase, Checkbox, FormControlLabel, IconButton, InputAdornment, Slider, TextField, Tooltip, Typography } from "@mui/material";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ReplayIcon from "@mui/icons-material/Replay";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import { UnitSystem } from "../../../utils/units";
import ModelClassSelect from "./ModelClassSelect";
import { AccumulationChart, CurveParams } from "./FuelCurve";
import { McGroup, PALETTE, RowInfo, VtField, VtRow, fmtField, loadUnit, maxLoad, num, sliderLoad, yearsTo } from "./vtModel";
import {
  FAINT, LINE, MUTED, RED_TEXT, TEXT, btnPlain, btnSmall, capSx, checkboxSx, chipSx, fieldSx,
  iconBtnSx, labelSx, linkBtnSx, panelSx, sliderSx,
} from "./vtTheme";

interface VegTypeInspectorProps {
  info: RowInfo | null;
  chip: { label: string; tone: "mod" | "new" | "del" } | null;
  groups: McGroup[];
  unit: UnitSystem;
  others: (CurveParams & { key: string })[];
  compare: boolean;
  onCompare: (on: boolean) => void;
  onPatch: (patch: Partial<VtRow>, coalesce?: string) => void;
  onRevertField: (field: VtField) => void;
  onRevertAll: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onRestore: () => void;
  maxHeight: string;
}

const Hint = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ fontSize: 12, color: MUTED, mt: 1, display: "flex", gap: 0.75, alignItems: "center", flexWrap: "wrap" }}>{children}</Box>
);
const ErrText = ({ children }: { children: React.ReactNode }) => (
  <Typography sx={{ fontSize: 12, color: RED_TEXT, mt: 0.75 }}>{children}</Typography>
);

/** The selected vegetation type: every field, its fuel curve against the
 *  others, and how long it takes to build back up. */
export default function VegTypeInspector({
  info, chip, groups, unit, others, compare, onCompare, onPatch, onRevertField, onRevertAll,
  onDuplicate, onDelete, onRestore, maxHeight,
}: VegTypeInspectorProps) {
  const shell = { ...panelSx, flex: "1 1 360px", minWidth: 300, position: "sticky" as const, top: 0, maxHeight, overflowY: "auto" as const };

  if (!info) {
    return (
      <Box component="aside" aria-label="Selected vegetation type" sx={shell}>
        <Typography sx={{ p: "48px 24px", textAlign: "center", fontSize: 13, color: MUTED }}>Select a vegetation type to see its fuel curve.</Typography>
      </Box>
    );
  }

  const { r, s, fields: f, errs } = info;
  const deleted = !!r.deleted;
  const L = num(r.max);
  const k = num(r.rate);
  const valid = !errs.max && !errs.rate && Number.isFinite(L) && Number.isFinite(k);
  const unitLabel = loadUnit(unit);
  const savedL = s ? num(s.max) : NaN;
  const savedK = s ? num(s.rate) : NaN;
  const showSaved = !!s && (f.max || f.rate) && Number.isFinite(savedL) && Number.isFinite(savedK);
  const inPalette = PALETTE.some((c) => c === r.color.toLowerCase());
  const swatches = inPalette ? PALETTE : [r.color, ...PALETTE.slice(0, -1)];
  const savedHint = (field: VtField) => (s && f[field] ? fmtField(field, s[field], unit) : "");

  const numberField = (opts: { id: string; label: string; field: "max" | "rate"; suffix: string; width: number; sliderMax: number; step: number }) => {
    const value = r[opts.field];
    const err = errs[opts.field];
    const hint = savedHint(opts.field);
    return (
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, mb: 1.25 }}>
          <Box component="label" htmlFor={opts.id} sx={labelSx}>{opts.label}</Box>
          <TextField
            id={opts.id}
            size="small"
            value={value}
            disabled={deleted}
            onChange={(e) => onPatch({ [opts.field]: e.target.value }, `${opts.field}:${r.key}`)}
            sx={{ width: opts.width }}
            slotProps={{
              input: {
                sx: { ...fieldSx({ mod: f[opts.field], err: !!err }), "& input": { textAlign: "right", fontVariantNumeric: "tabular-nums" } },
                endAdornment: <InputAdornment position="end" sx={{ "& p": { fontSize: 12, color: FAINT } }}>{opts.suffix}</InputAdornment>,
              },
              htmlInput: { inputMode: "decimal", "aria-invalid": !!err },
            }}
          />
        </Box>
        <Slider
          size="small"
          min={0}
          max={opts.sliderMax}
          step={opts.step}
          value={Number.isFinite(num(value)) ? Math.max(0, Math.min(opts.sliderMax, num(value))) : 0}
          disabled={deleted}
          onChange={(_, v) => onPatch({ [opts.field]: String(v as number) }, `${opts.field}:${r.key}`)}
          aria-label={`${opts.label} slider`}
          sx={sliderSx}
        />
        {err && <ErrText>{err}</ErrText>}
        {hint && <Hint>Saved value {hint} · <Button sx={linkBtnSx} onClick={() => onRevertField(opts.field)}>Revert</Button></Hint>}
      </Box>
    );
  };

  return (
    <Box component="aside" aria-label="Selected vegetation type" sx={shell}>
      <Box sx={{ p: "18px 20px 16px", borderBottom: `1px solid ${LINE}` }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <Box sx={{ width: 16, height: 16, borderRadius: 1, bgcolor: r.color, boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.2)" }} />
          <Typography sx={{ ...capSx, color: MUTED }}>Vegetation type</Typography>
          {chip && <Box sx={chipSx(chip.tone)}>{chip.label}</Box>}
          <Box sx={{ flex: 1 }} />
          {!!s && !deleted && info.changes.length > 0 && (
            <Tooltip title="Revert all changes"><IconButton sx={iconBtnSx} aria-label="Revert all changes to this type" onClick={onRevertAll}><ReplayIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip>
          )}
          <Tooltip title="Duplicate"><span><IconButton sx={iconBtnSx} aria-label="Duplicate this type" disabled={deleted} onClick={onDuplicate}><ContentCopyOutlinedIcon sx={{ fontSize: 16 }} /></IconButton></span></Tooltip>
          <Tooltip title="Delete"><span><IconButton sx={iconBtnSx} aria-label="Delete this type" disabled={deleted} onClick={onDelete}><DeleteOutlineIcon sx={{ fontSize: 18 }} /></IconButton></span></Tooltip>
        </Box>
        <Box component="label" htmlFor="vt-name" sx={labelSx}>Name</Box>
        <TextField
          id="vt-name"
          fullWidth
          multiline
          minRows={2}
          value={r.name}
          disabled={deleted}
          onChange={(e) => onPatch({ name: e.target.value }, `name:${r.key}`)}
          sx={{ mt: 0.75 }}
          slotProps={{ input: { sx: { ...fieldSx({ mod: f.name, err: !!errs.name }), fontSize: 14, fontWeight: 500, py: 1, lineHeight: 1.4, ".MuiInputBase-input": { py: 0 } } } }}
        />
        {errs.name && <ErrText>{errs.name}</ErrText>}
        {r.fromLibrary && !s && (
          <Hint><MenuBookOutlinedIcon sx={{ fontSize: 15 }} />Added from your library. Adjust it before saving if you need to.</Hint>
        )}
      </Box>

      {deleted && (
        <Box sx={{ m: "16px 20px 0", p: "12px 14px", borderRadius: 2, bgcolor: "rgba(239,83,80,0.08)", border: "1px solid rgba(239,83,80,0.3)", fontSize: 13, display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ flex: 1 }}>Marked for deletion. It is removed when you save.</Box>
          <Button sx={{ ...btnPlain, ...btnSmall }} onClick={onRestore}>Restore</Button>
        </Box>
      )}

      <Box sx={{ p: "18px 20px 20px", display: "flex", flexDirection: "column", gap: 2.75 }}>
        <Box>
          <Typography sx={{ ...labelSx, mb: 1 }}>Map colour</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
            {swatches.map((c) => {
              const on = c.toLowerCase() === r.color.toLowerCase();
              return (
                <ButtonBase
                  key={c}
                  aria-label={`Colour ${c}`}
                  aria-pressed={on}
                  disabled={deleted}
                  onClick={() => onPatch({ color: c })}
                  sx={{
                    width: 26, height: 26, borderRadius: 1.75, bgcolor: c, border: `2px solid ${on ? TEXT : "transparent"}`,
                    boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.18)", transition: "transform .1s",
                    "&:hover": { transform: "scale(1.08)" }, "&.Mui-disabled": { opacity: 0.45 },
                  }}
                />
              );
            })}
            <Tooltip title="Custom colour">
              <Box
                component="input"
                type="color"
                value={r.color}
                disabled={deleted}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onPatch({ color: e.target.value.toLowerCase() }, `color:${r.key}`)}
                aria-label="Custom colour"
                sx={{
                  width: 26, height: 26, p: 0, border: "1px dashed rgba(255,255,255,0.35)", borderRadius: 1.75, background: "transparent",
                  cursor: deleted ? "default" : "pointer",
                  "&::-webkit-color-swatch-wrapper": { padding: "3px" }, "&::-webkit-color-swatch": { border: "none", borderRadius: "4px" },
                }}
              />
            </Tooltip>
          </Box>
        </Box>

        <Box>
          <Box component="label" htmlFor="vt-mc" sx={labelSx}>Model class</Box>
          <Box sx={{ mt: 0.75 }}>
            <ModelClassSelect id="vt-mc" variant="field" value={r.mc} groups={groups} mod={f.mc} disabled={deleted} onChange={(mc) => onPatch({ mc })} />
          </Box>
          {savedHint("mc") && <Hint>Saved value {savedHint("mc")} · <Button sx={linkBtnSx} onClick={() => onRevertField("mc")}>Revert</Button></Hint>}
        </Box>

        {numberField({ id: "vt-max", label: "Max fuel load", field: "max", suffix: unitLabel, width: unit === "imperial" ? 150 : 124, sliderMax: sliderLoad(unit), step: unit === "imperial" ? 0.25 : 0.5 })}
        {numberField({ id: "vt-rate", label: "Accumulation rate (k)", field: "rate", suffix: "/yr", width: 124, sliderMax: 0.5, step: 0.005 })}

        <Box sx={{ borderTop: `1px solid ${LINE}`, pt: 2.25 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Fuel load accumulation</Typography>
            <FormControlLabel
              control={<Checkbox size="small" sx={checkboxSx} checked={compare} onChange={(e) => onCompare(e.target.checked)} />}
              label="Show other types"
              sx={{ mr: 0, "& .MuiFormControlLabel-label": { fontSize: 12, color: MUTED } }}
            />
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px 14px", mt: 0.5, mb: 0.5, fontSize: 11, color: MUTED }}>
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}><Box sx={{ width: 14, height: 2, bgcolor: "#00d4ad" }} />Current</Box>
            {showSaved && <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}><Box sx={{ width: 14, borderTop: "2px dashed rgba(255,255,255,0.75)" }} />Saved</Box>}
            {compare && <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}><Box sx={{ width: 14, height: 2, bgcolor: "rgba(255,255,255,0.25)" }} />Other types</Box>}
          </Box>
          <AccumulationChart
            current={valid ? { max: L, rate: k } : null}
            saved={showSaved ? { max: savedL, rate: savedK } : null}
            others={compare ? others : []}
            unit={unitLabel}
            cap={maxLoad(unit)}
          />
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1, mt: 2 }}>
            {[["Reaches 50% of max", valid ? yearsTo(k, 2) : "—"], ["Reaches 90% of max", valid ? yearsTo(k, 10) : "—"]].map(([label, v]) => (
              <Box key={label} sx={{ bgcolor: "rgba(0,0,0,0.2)", border: `1px solid ${LINE}`, borderRadius: 2, p: "10px 12px" }}>
                <Typography sx={labelSx}>{label}</Typography>
                <Typography sx={{ fontSize: 16, fontWeight: 600, mt: 0.25, fontVariantNumeric: "tabular-nums" }}>{v}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
